/**
 * AnalyzifyProvider
 * React context provider for Analyzify SDK
 */

import React, { createContext, useEffect, useState, useRef, useMemo, useCallback, Suspense, lazy, type ReactNode } from 'react';
import { Analyzify } from '../core/analyzify';
import type { AnalyzifyOptions, PageContext } from '../types/config';
import type { ConsentState } from '../types/events';
import type { StorefrontProduct, StorefrontCart, StorefrontOrder } from '../types/storefront';
import type { HeadlessMetafield } from '../types/headless-config';

/**
 * ShopifyCookieManager is lazy-loaded so its static `@shopify/hydrogen-react`
 * import is emitted as a separate async chunk. This keeps the optional peer
 * dependency out of the main ./react bundle's static import graph — the chunk
 * is only fetched when `shopifyNativeAnalytics` is configured.
 */
const ShopifyCookieManager = lazy(() =>
  // Degrade gracefully if the async chunk fails to load (deploy skew, transient
  // network failure). Resolving to a null-rendering component keeps React.lazy
  // from re-throwing into the host tree — analytics must never crash the app.
  import('./shopify-cookie-manager').catch(() => ({ default: () => null }))
);

export interface AnalyzifyContextValue {
  analyzify: Analyzify | null;
  isReady: boolean;
  setPageContext: (context: PageContext) => void;
  /** Tell the SDK about the current cart (call on every cart change). See Analyzify.setCart(). */
  setCart: (cart: StorefrontCart | null | undefined) => void;
  track: {
    viewItem: (product: StorefrontProduct, variantId?: string) => void;
    viewItemList: (products: StorefrontProduct[], listName: string, listId?: string) => void;
    selectItem: (product: StorefrontProduct, listName: string, index?: number, listId?: string | number) => void;
    addToCart: (product: StorefrontProduct, quantity: number, cart: StorefrontCart, variantId?: string) => void;
    removeFromCart: (product: StorefrontProduct, quantity: number, cart: StorefrontCart, variantId?: string) => void;
    viewCart: (cart: StorefrontCart) => void;
    beginCheckout: (cart: StorefrontCart) => void;
    search: (term: string, results: StorefrontProduct[], resultsCount: number) => void;
    generateLead: (formData: Record<string, any>) => void;
    addToWishlist: (product: StorefrontProduct, variantId?: string) => void;
    variantChange: (product: StorefrontProduct, variantId: string) => void;
    homepage: () => void;
    purchase: (order: StorefrontOrder) => void;
    navClick: (title: string, url: string, position: string) => void;
  };
  consent: {
    setConsent: (state: Partial<ConsentState>) => void;
    getConsent: () => ConsentState;
    canTrack: () => boolean;
    onChange: (callback: () => void) => () => void;
  };
}

export const AnalyzifyContext = createContext<AnalyzifyContextValue | null>(null);

export interface AnalyzifyProviderProps {
  children: ReactNode;
  /** Shop domain (e.g., 'your-store.myshopify.com') */
  shopDomain: string;
  /** Storefront API access token (required unless config is provided) */
  storefrontToken?: string;
  /** Pre-built raw headless config to skip Storefront API fetch (useful for testing/mocking) */
  config?: HeadlessMetafield;
  /** Currency code override */
  currency?: string;
  /** Locale override */
  locale?: string;
  /** Country code override */
  country?: string;
  /** Enable debug logging */
  debug?: boolean;
  /** Custom script attribute for loaded scripts */
  scriptAttribute?: string;
  /**
   * GTM container ID override (e.g., 'GTM-XXXXXXX')
   * Required for custom GTM setups where GA4 is managed through GTM.
   * If not provided, the SDK reads `gtm_id` from the headless config.
   */
  gtmContainerId?: string;
  /** Custom GTM server URL for standard server-side GTM */
  gtmServerUrl?: string;
  /** Full GTM script URL for Stape server-side GTM */
  gtmScriptUrl?: string;
  /**
   * Google Consent Mode v2 defaults
   * Sets initial consent state BEFORE any Google scripts load.
   */
  consentDefaults?: {
    ad_storage?: 'granted' | 'denied';
    ad_user_data?: 'granted' | 'denied';
    analytics_storage?: 'granted' | 'denied';
    functionality_storage?: 'granted' | 'denied';
    personalization_storage?: 'granted' | 'denied';
    security_storage?: 'granted' | 'denied';
    wait_for_update?: number;
    ads_data_redaction?: boolean;
    /** Fallback consent posture on CMP timeout / no CMP. Default 'grant'. */
    timeoutFallback?: 'grant' | 'deny';
  };
  /**
   * Cookiebot CMP ID for consent banner
   */
  cookiebotId?: string;
  /** Storefront root domain for Shopify Privacy API (e.g., 'example.com') */
  storefrontRootDomain?: string;
  /** Checkout root domain for Shopify Privacy API (defaults to storefrontRootDomain) */
  checkoutRootDomain?: string;
  /**
   * Enable Shopify native analytics (populates Shopify admin dashboard).
   * When provided, the SDK sends events to Shopify's monorail endpoint
   * and fetches tracking tokens via Storefront API server-timing headers.
   */
  shopifyNativeAnalytics?: {
    shopId: string;
    currency?: string;
    acceptedLanguage?: string;
    storefrontId?: string;
    /** Geography-driven compliance flags (host-app responsibility). Defaults: true/true/false. */
    gdprEnforced?: boolean;
    ccpaEnforced?: boolean;
    saleOfDataAllowed?: boolean;
  };
  /**
   * Let the CDN scripts detect ecommerce clicks on their own (default false).
   * See AnalyzifyOptions.autoDetectClicks.
   */
  autoDetectClicks?: boolean;
  /**
   * Write Analyzify's attribution attributes onto the cart via the Storefront
   * API (default true). See AnalyzifyOptions.cartAttributes.
   */
  cartAttributes?: boolean;
  /** Callback when SDK is ready */
  onReady?: () => void;
  /** Callback when SDK encounters an error */
  onError?: (error: Error) => void;
  /**
   * Delay SDK initialization to reduce impact on page performance.
   * - `'idle'`: Uses requestIdleCallback (falls back to 2s setTimeout)
   * - `number`: Delays init by the specified milliseconds
   * - `undefined`: No delay (default, backward compatible)
   */
  initDelay?: 'idle' | number;
}

export function AnalyzifyProvider({
  children,
  shopDomain,
  storefrontToken,
  config,
  currency,
  locale,
  country,
  debug,
  scriptAttribute,
  gtmContainerId,
  gtmServerUrl,
  gtmScriptUrl,
  consentDefaults,
  cookiebotId,
  storefrontRootDomain,
  checkoutRootDomain,
  shopifyNativeAnalytics,
  autoDetectClicks,
  cartAttributes,
  onReady,
  onError,
  initDelay,
}: AnalyzifyProviderProps): React.JSX.Element {
  const [isReady, setIsReady] = useState(false);
  const [consentGranted, setConsentGranted] = useState(false);
  const analyzifyRef = useRef<Analyzify | null>(null);
  // Cart handed to setCart() before the SDK instance exists (initDelay)
  const pendingCartRef = useRef<StorefrontCart | null | undefined>(undefined);

  // Object props are compared by content: a new object literal on every
  // render must not tear down and re-create the SDK.
  const configKey = JSON.stringify(config ?? null);
  const consentDefaultsKey = JSON.stringify(consentDefaults ?? null);
  const shopifyNativeAnalyticsKey = JSON.stringify(shopifyNativeAnalytics ?? null);

  // Use refs for callbacks to avoid stale closures without triggering re-init
  const onReadyRef = useRef(onReady);
  const onErrorRef = useRef(onError);

  // Keep refs in sync with latest props
  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    // Skip on server
    if (typeof window === 'undefined') return;

    // CRITICAL: Track whether this effect is still active.
    // In React Strict Mode (dev), effects fire twice. Without this guard,
    // the first mount's onReady could trigger setIsReady before the second
    // mount's Analyzify instance (the one in analyzifyRef) is initialized.
    let active = true;
    let cleanupDelay: (() => void) | undefined;

    const doInit = () => {
      if (!active) return;

      // Create Analyzify instance
      const options: AnalyzifyOptions = {
        shopDomain,
        storefrontToken,
        config,
        currency,
        locale,
        country,
        debug,
        scriptAttribute,
        gtmContainerId,
        gtmServerUrl,
        gtmScriptUrl,
        consentDefaults,
        cookiebotId,
        storefrontRootDomain,
        checkoutRootDomain,
        shopifyNativeAnalytics,
        autoDetectClicks,
        cartAttributes,
        onReady: () => {
          if (active) {
            setIsReady(true);
            onReadyRef.current?.();
          }
        },
        onError: (error) => {
          if (active) {
            onErrorRef.current?.(error);
          }
        },
      };

      analyzifyRef.current = new Analyzify(options);
      if (pendingCartRef.current !== undefined) {
        analyzifyRef.current.setCart(pendingCartRef.current);
        pendingCartRef.current = undefined;
      }

      // Expose SDK instance for debug panel interception
      // The debug panel looks for window.__analyzify_sdk__ to intercept track methods
      (window as any).__analyzify_sdk__ = analyzifyRef.current;

      // Initialize
      analyzifyRef.current.init().catch((error) => {
        if (active) {
          console.error('Analyzify initialization failed:', error);
          onErrorRef.current?.(error);
        }
      });
    };

    // Support delayed initialization to reduce impact on page performance
    if (initDelay === 'idle') {
      if ('requestIdleCallback' in window) {
        const id = requestIdleCallback(doInit, { timeout: 5000 });
        cleanupDelay = () => cancelIdleCallback(id);
      } else {
        const timer = setTimeout(doInit, 2000);
        cleanupDelay = () => clearTimeout(timer);
      }
    } else if (typeof initDelay === 'number' && initDelay > 0) {
      const timer = setTimeout(doInit, initDelay);
      cleanupDelay = () => clearTimeout(timer);
    } else {
      doInit();
    }

    // Cleanup: mark this effect inactive so stale callbacks don't fire and
    // cancel any pending init-delay timer. We deliberately do NOT call
    // destroy() here: under StrictMode double-mount / provider remount the
    // discarded instance shares window.analyzify (and the installed
    // DataLayerProxy) with the reused instance, so tearing down its
    // ConsentManager would orphan the buffered-event flush wiring and strand
    // consent-gated events. destroy() remains a public method for consumers
    // that explicitly own the SDK lifecycle (e.g. micro-frontends, tests).
    return () => {
      active = false;
      cleanupDelay?.();
    };
    // Object props are tracked by content (…Key) — see above
  }, [initDelay, shopDomain, storefrontToken, configKey, currency, locale, country, debug, scriptAttribute, gtmContainerId, gtmServerUrl, gtmScriptUrl, consentDefaultsKey, cookiebotId, storefrontRootDomain, checkoutRootDomain, shopifyNativeAnalyticsKey, autoDetectClicks, cartAttributes]);

  // Subscribe to consent changes for ShopifyCookieManager re-rendering.
  // Without this, consent changes (e.g., Cookiebot accept) after SDK init
  // would never propagate to useShopifyCookies.
  useEffect(() => {
    if (!isReady || !analyzifyRef.current) return;
    setConsentGranted(analyzifyRef.current.consent.canTrack());
    const unsubscribe = analyzifyRef.current.consent.onChange(() => {
      setConsentGranted(analyzifyRef.current?.consent.canTrack() ?? false);
    });
    return () => {
      unsubscribe?.();
    };
  }, [isReady]);

  // Stable callback refs for tracking methods
  const setPageContext = useCallback((context: PageContext) => {
    analyzifyRef.current?.setPageContext(context);
  }, []);

  const setCart = useCallback((cart: StorefrontCart | null | undefined) => {
    if (analyzifyRef.current) {
      analyzifyRef.current.setCart(cart);
    } else {
      pendingCartRef.current = cart;
    }
  }, []);

  const trackViewItem = useCallback((product: StorefrontProduct, variantId?: string) => {
    analyzifyRef.current?.track.viewItem(product, variantId);
  }, []);

  const trackViewItemList = useCallback((products: StorefrontProduct[], listName: string, listId?: string) => {
    analyzifyRef.current?.track.viewItemList(products, listName, listId);
  }, []);

  const trackSelectItem = useCallback((product: StorefrontProduct, listName: string, index?: number, listId?: string | number) => {
    analyzifyRef.current?.track.selectItem(product, listName, index, listId);
  }, []);

  const trackAddToCart = useCallback((product: StorefrontProduct, quantity: number, cart: StorefrontCart, variantId?: string) => {
    analyzifyRef.current?.track.addToCart(product, quantity, cart, variantId);
  }, []);

  const trackRemoveFromCart = useCallback((product: StorefrontProduct, quantity: number, cart: StorefrontCart, variantId?: string) => {
    analyzifyRef.current?.track.removeFromCart(product, quantity, cart, variantId);
  }, []);

  const trackViewCart = useCallback((cart: StorefrontCart) => {
    analyzifyRef.current?.track.viewCart(cart);
  }, []);

  const trackBeginCheckout = useCallback((cart: StorefrontCart) => {
    analyzifyRef.current?.track.beginCheckout(cart);
  }, []);

  const trackSearch = useCallback((term: string, results: StorefrontProduct[], resultsCount: number) => {
    analyzifyRef.current?.track.search(term, results, resultsCount);
  }, []);

  const trackGenerateLead = useCallback((formData: Record<string, any>) => {
    analyzifyRef.current?.track.generateLead(formData);
  }, []);

  const trackAddToWishlist = useCallback((product: StorefrontProduct, variantId?: string) => {
    analyzifyRef.current?.track.addToWishlist(product, variantId);
  }, []);

  const trackVariantChange = useCallback((product: StorefrontProduct, variantId: string) => {
    try {
      analyzifyRef.current?.track.variantChange(product, variantId);
    } catch {
      // Silently catch CDN errors (TypeError/ReferenceError expected in headless SPA)
    }
  }, []);

  const trackHomepage = useCallback(() => {
    analyzifyRef.current?.track.homepage();
  }, []);

  const trackPurchase = useCallback((order: StorefrontOrder) => {
    analyzifyRef.current?.track.purchase(order);
  }, []);

  const trackNavClick = useCallback((title: string, url: string, position: string) => {
    analyzifyRef.current?.track.navClick(title, url, position);
  }, []);

  const setConsent = useCallback((state: Partial<ConsentState>) => {
    analyzifyRef.current?.consent.setConsent(state);
  }, []);

  const getConsent = useCallback(() => {
    return analyzifyRef.current?.consent.getConsent() ?? {
      analytics: false,
      marketing: false,
      preferences: false,
    };
  }, []);

  const canTrack = useCallback(() => {
    return analyzifyRef.current?.consent.canTrack() ?? false;
  }, []);

  const onConsentChange = useCallback((callback: () => void): (() => void) => {
    return analyzifyRef.current?.consent.onChange(callback) ?? (() => {});
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<AnalyzifyContextValue>(
    () => ({
      analyzify: analyzifyRef.current,
      isReady,
      setPageContext,
      setCart,
      track: {
        viewItem: trackViewItem,
        viewItemList: trackViewItemList,
        selectItem: trackSelectItem,
        addToCart: trackAddToCart,
        removeFromCart: trackRemoveFromCart,
        viewCart: trackViewCart,
        beginCheckout: trackBeginCheckout,
        search: trackSearch,
        generateLead: trackGenerateLead,
        addToWishlist: trackAddToWishlist,
        variantChange: trackVariantChange,
        homepage: trackHomepage,
        purchase: trackPurchase,
        navClick: trackNavClick,
      },
      consent: {
        setConsent,
        getConsent,
        canTrack,
        onChange: onConsentChange,
      },
    }),
    [
      isReady,
      setPageContext,
      setCart,
      trackViewItem,
      trackViewItemList,
      trackSelectItem,
      trackAddToCart,
      trackRemoveFromCart,
      trackViewCart,
      trackBeginCheckout,
      trackSearch,
      trackGenerateLead,
      trackAddToWishlist,
      trackVariantChange,
      trackHomepage,
      trackPurchase,
      trackNavClick,
      setConsent,
      getConsent,
      canTrack,
      onConsentChange,
    ]
  );

  return (
    <AnalyzifyContext.Provider value={contextValue}>
      {shopifyNativeAnalytics && (
        <Suspense fallback={null}>
          <ShopifyCookieManager
            hasConsent={consentGranted}
            storefrontAccessToken={storefrontToken}
            checkoutDomain={checkoutRootDomain}
          />
        </Suspense>
      )}
      {children}
    </AnalyzifyContext.Provider>
  );
}
